'use client';
import { FunctionComponent, useEffect, useRef, useState } from 'react';
import { OriginString, CheckoutState } from '@shared/types';
import { PublicCourse, PublicEducator } from '@cademy-package/sdk-component-types';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { CheckoutFlow } from './flows';
import { useCheckout } from './useCheckout/index';
import Alert from '../Alert';
import Spinner from '../Spinner';
import styles from './styles.module.scss';
import Unauthorised from './Unauthorised';

export type CheckoutProps = {
    course: PublicCourse;
    educator: PublicEducator;
    origin: OriginString;
    flow: CheckoutState['flow'];
    occurrenceId?: string;
    checkoutId?: string;
    onCheckoutIdChange?: (newCheckoutId: string) => void;
};

export const Checkout: FunctionComponent<CheckoutProps> = ({
    course,
    educator,
    origin,
    flow,
    occurrenceId,
    checkoutId,
    onCheckoutIdChange,
}) => {
    const [queryClient] = useState(new QueryClient());
    const { isReady, isLoading, checkoutState, submitResponse, error } = useCheckout({
        courseId: course._id,
        origin,
        flow,
        checkoutId,
        occurrenceId,
        onCheckoutIdChange,
    });

    const alertRef = useRef<HTMLDivElement | null>(null);
    useEffect(() => {
        if (alertRef.current) {
            alertRef.current.focus();
            alertRef.current.scrollIntoView();
        }
    }, [error]);

    if (
        error &&
        error.type === `${process.env.NEXT_PUBLIC_CADEMY_ERROR_PREFIX}/checkout/unauthorised`
    ) {
        return <Unauthorised origin={origin} educatorId={educator._id} />;
    }

    if (isReady === false) {
        return (
            <div>
                {error ? <Alert preset="danger">{error.title}</Alert> : null}
                <Spinner innerClassName={styles.spinnerIn} outerClassName={styles.spinnerOut} />
            </div>
        );
    }

    return (
        <QueryClientProvider client={queryClient}>
            {error ? (
                <Alert preset="danger" ref={alertRef}>
                    {error.detail ? `${error.title}: ${error.detail}` : error.title}
                </Alert>
            ) : null}

            <div style={isLoading ? { filter: 'blur(2px)' } : {}}>
                <CheckoutFlow
                    course={course}
                    educator={educator}
                    checkoutState={checkoutState}
                    submitResponse={submitResponse}
                    isLoading={isLoading}
                    error={error}
                />
            </div>
        </QueryClientProvider>
    );
};
